/*
  FollowUpFields — schema-driven EP Follow-Up capture (2026-02
  consolidation).

  Historically this component owned its own field list (health-change
  chips, exercises table, observations table). It now renders directly
  from the ``ep_follow_up`` module registered in
  ``backend/assessments/module_defs.py``, so the SOAP-session Follow-Up
  UI and the standalone Assessments tab share one canonical schema.

  Value shape emitted on ``onChange``::

      {
        epf_since_last: "Better" | ...,
        epf_new_symptoms: "…",
        epf_client_reported_change: "…",
        epf_health_changes: [{type, notes}, ...],
        epf_exercises: [{name, category, load, sets, reps, rpe, response, notes}, ...],
        epf_observations: [{category, text}, ...],
        epf_plan_summary: "…",
        epf_progression_trigger: "…",
        epf_next_review_weeks: 4,
      }

  Backend ``_format_follow_up_for_prompt`` (routes/soap_notes.py) reads
  either the new ``epf_*`` keys or the legacy blob, so existing SOAP
  notes continue to work.
*/
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Copy, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FieldRenderer } from "@/components/assessment/FieldRenderer";

function CollapsibleSection({ title, defaultOpen = true, right, children, testKey }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-md border border-zinc-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-50"
        data-testid={`fu-section-toggle-${testKey || title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-700">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          {title}
        </span>
        {right}
      </button>
      {open && <div className="p-3 pt-2 space-y-3 border-t border-zinc-100">{children}</div>}
    </div>
  );
}

function PreviousSummary({ note }) {
  if (!note) {
    return (
      <div className="text-xs text-zinc-500 italic">
        No previous Exercise Physiology consultation on file for this client.
      </div>
    );
  }
  return (
    <div className="text-xs space-y-2" data-testid="fu-previous-summary">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className="bg-zinc-100 text-zinc-700 border-zinc-200 border">
          {note.appointment_type || "Previous consult"}
        </Badge>
        <span className="text-zinc-500">
          {note.created_at ? new Date(note.created_at).toLocaleString() : ""}
        </span>
        {note.signed_off ? (
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border">Signed</Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200 border">Draft</Badge>
        )}
      </div>
      {note.subjective && (
        <div>
          <div className="text-2xs uppercase tracking-wider text-zinc-500 font-semibold">Subjective</div>
          <div className="whitespace-pre-wrap line-clamp-4 text-zinc-800">{note.subjective}</div>
        </div>
      )}
      {note.plan && (
        <div>
          <div className="text-2xs uppercase tracking-wider text-zinc-500 font-semibold">Previous plan</div>
          <div className="whitespace-pre-wrap line-clamp-3 text-zinc-800">{note.plan}</div>
        </div>
      )}
      {(note.exercises || []).length > 0 && (
        <div>
          <div className="text-2xs uppercase tracking-wider text-zinc-500 font-semibold">
            Exercises last session ({note.exercises.length})
          </div>
          <ul className="list-disc pl-5 text-zinc-800">
            {note.exercises.slice(0, 6).map((e, i) => (
              <li key={i}>
                <strong>{e.name}</strong>
                {e.sets ? ` — ${e.sets}` : ""}
                {e.reps ? ` × ${e.reps}` : ""}
                {e.load ? ` @ ${e.load}` : ""}
              </li>
            ))}
            {note.exercises.length > 6 && (
              <li className="text-zinc-500 italic">…and {note.exercises.length - 6} more</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// Legacy → schema migration. Any session doc created before Feb 2026
// stores the follow-up under the old blob keys; convert on read so the
// schema-driven editor picks up where the practitioner left off.
function normaliseValue(value) {
  if (!value || typeof value !== "object") return {};
  if (Object.keys(value).some((k) => k.startsWith("epf_"))) return value;
  return {
    epf_since_last: value.since_last || "",
    epf_new_symptoms: value.new_symptoms || "",
    epf_client_reported_change: value.client_reported_change || "",
    epf_health_changes: Array.isArray(value.health_changes) ? value.health_changes : [],
    epf_exercises: Array.isArray(value.exercises) ? value.exercises : [],
    epf_observations: Array.isArray(value.clinical_observations) ? value.clinical_observations : [],
  };
}

export default function FollowUpFields({ clientEmail, value, onChange, disabled }) {
  const [module, setModule] = useState(null);
  const [prevNote, setPrevNote] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [loadingModule, setLoadingModule] = useState(true);

  const state = useMemo(() => normaliseValue(value), [value]);

  // Load the ep_follow_up schema once. Fields, categories and choice
  // options all live server-side now.
  useEffect(() => {
    setLoadingModule(true);
    api.get("/assessments/modules/ep_follow_up")
      .then((r) => setModule(r.data || null))
      .catch(() => setModule(null))
      .finally(() => setLoadingModule(false));
  }, []);

  // Pre-consultation summary — most recent EP note for the client.
  useEffect(() => {
    if (!clientEmail) { setPrevNote(null); return; }
    setLoadingPrev(true);
    api.get(`/soap-notes/clients/${encodeURIComponent(clientEmail)}/last-ep-note`)
      .then((r) => setPrevNote(r.data?.note || null))
      .catch(() => setPrevNote(null))
      .finally(() => setLoadingPrev(false));
  }, [clientEmail]);

  const setField = (key, v) => onChange({ ...state, [key]: v });

  const buildResp = (key) => {
    const raw = state[key];
    if (raw === undefined || raw === null) return { value: "" };
    return { value: raw };
  };

  function copyExercisesFromPrevious() {
    if (!prevNote?.exercises?.length) {
      toast.info("No exercises to copy", { description: "The previous EP consult has no exercises recorded." });
      return;
    }
    const seeded = prevNote.exercises.map((e) => ({
      name: e.name || "",
      category: e.category || "",
      load: e.load || "",
      sets: e.sets || "",
      reps: e.reps || "",
      rpe: "",
      response: "",
      notes: e.notes || "",
    }));
    const merged = [...(state.epf_exercises || []), ...seeded];
    setField("epf_exercises", merged);
    toast.success(`Copied ${seeded.length} exercise${seeded.length === 1 ? "" : "s"} from the previous consult`);
  }

  if (loadingModule) {
    return (
      <div className="rounded-md border border-zinc-200 bg-white p-3 text-xs text-zinc-500 italic" data-testid="follow-up-fields-loading">
        Loading EP Follow-Up form…
      </div>
    );
  }
  if (!module) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="follow-up-fields-error">
        Unable to load the EP Follow-Up assessment module. Check that the backend is running.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="follow-up-fields">
      <CollapsibleSection
        title="Pre-consultation summary"
        testKey="pre-consultation-summary"
        defaultOpen
        right={loadingPrev ? <span className="text-2xs text-zinc-400">Loading…</span> : null}
      >
        <PreviousSummary note={prevNote} />
      </CollapsibleSection>

      {module.sections.map((section) => {
        const isExerciseSection = section.key === "epf_exercise_session";
        const rightSlot = isExerciseSection ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); copyExercisesFromPrevious(); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                copyExercisesFromPrevious();
              }
            }}
            className={`inline-flex items-center gap-1 text-2xs px-2 py-1 rounded-md border ${
              prevNote?.exercises?.length
                ? "border-zinc-300 hover:bg-zinc-100 text-zinc-700 cursor-pointer"
                : "border-zinc-200 text-zinc-400 cursor-not-allowed"
            }`}
            aria-disabled={disabled || !prevNote?.exercises?.length}
            data-testid="fu-copy-exercises-btn"
          >
            <Copy className="h-3 w-3" /> Copy previous
          </span>
        ) : null;
        const defaultOpen = ["epf_status", "epf_exercise_session"].includes(section.key);
        return (
          <CollapsibleSection
            key={section.key}
            title={section.label}
            testKey={section.key}
            defaultOpen={defaultOpen}
            right={rightSlot}
          >
            <div className="space-y-3">
              {section.fields.map((field) => (
                <div key={field.key} data-testid={`fu-field-${field.key}`}>
                  <div className="text-2xs font-medium text-zinc-700 mb-0.5 flex items-center gap-2">
                    {field.label}
                    {field.type === "table" ? <ClipboardList className="h-3 w-3 text-zinc-400" /> : null}
                  </div>
                  <FieldRenderer
                    field={field}
                    resp={buildResp(field.key)}
                    onChange={(k, v) => setField(k, v)}
                    readOnly={!!disabled}
                  />
                  {field.help ? (
                    <div className="text-2xs text-zinc-500 mt-0.5">{field.help}</div>
                  ) : null}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        );
      })}
    </div>
  );
}

// Small helper the parent uses to detect "is this an EP Follow-Up?"
// via either the appointment-type string or a template that targets it.
export function isEpFollowUp({ appointmentType, template }) {
  const at = (appointmentType || "").toLowerCase();
  if (at.includes("follow-up") && at.includes("exercise physiology")) return true;
  if (at.includes("follow up") && at.includes("exercise physiology")) return true;
  if (at.includes("ep") && (at.includes("follow-up") || at.includes("follow up") || at.includes("review"))) return true;
  if (template?.name?.toLowerCase().includes("follow-up")
      && template?.name?.toLowerCase().includes("exercise physiology")) return true;
  return false;
}
